import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Mail, Phone, MessageCircle } from "lucide-react";

const Footer = () => {
    const { t, i18n } = useTranslation();

    return (
        <footer className="bg-primary text-white" role="contentinfo">
            <div className="container mx-auto py-12 px-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    {/* Logo and Social Media Section */}
                    <div className="flex flex-col items-center md:items-start space-y-6">
                        {/* Logo */}
                        <div>
                            <img
                                src="/assets/Logo_svg.svg"
                                alt={t("footer.companyLogoAlt") || "NawasTech - نواس تك"}
                                className="w-[180px] md:w-[200px] h-auto brightness-0 invert"
                                width="200"
                                height="70"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                        
                        {/* Social Media Icons */}
                        <div className="flex flex-col items-center md:items-start space-y-4">
                            <h3 className="text-lg font-semibold">{t("footer.followUs")}</h3>
                            <div className="flex justify-center md:justify-start gap-4">
                                {/* Email */}
                                <a
                                    href="mailto:info@nawastech.com"
                                    className="w-12 h-12 bg-white/10 hover:bg-accent hover:scale-110 transition-all duration-300 rounded-full flex items-center justify-center group"
                                    aria-label={t("footer.emailUs")}>
                                    <Mail className="w-5 h-5 text-white group-hover:text-primary" />
                                </a>
                                
                                {/* Phone */}
                                <a
                                    href="tel:+966537400097"
                                    className="w-12 h-12 bg-white/10 hover:bg-accent hover:scale-110 transition-all duration-300 rounded-full flex items-center justify-center group"
                                    aria-label={t("footer.callUs")}>
                                    <Phone className="w-5 h-5 text-white group-hover:text-primary" />
                                </a>
                                
                                {/* WhatsApp */}
                                <a
                                    href="https://wa.me/966537400097"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-12 h-12 bg-white/10 hover:bg-green-500 hover:scale-110 transition-all duration-300 rounded-full flex items-center justify-center group"
                                    aria-label={t("footer.whatsapp")}>
                                    <MessageCircle className="w-5 h-5 text-white group-hover:text-white" />
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Navigation Links */}
                    <div className="flex flex-col items-center md:items-start space-y-4">
                        <h3 className="text-lg font-semibold">{t("footer.quickLinks")}</h3>
                        <nav className="flex flex-col space-y-3">
                
                            <Link 
                                to="/features" 
                                className="text-sm hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-primary rounded-sm px-1 py-1">
                                {t("header.features")}
                            </Link>
                            <Link 
                                to="/pricing" 
                                className="text-sm hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-primary rounded-sm px-1 py-1">
                                {t("header.pricing")}
                            </Link>
                            <Link 
                                to="/contact-us" 
                                className="text-sm hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-primary rounded-sm px-1 py-1">
                                {t("header.contactUs")}
                            </Link>
                        </nav>
                    </div>

                    {/* Contact Information */}
                    <div className="flex flex-col items-center md:items-start space-y-4">
                        <h3 className="text-lg font-semibold">{t("footer.contactInfo")}</h3>
                        <div className="flex flex-col space-y-3">
                            <div className="flex items-center gap-3">
                                <Mail className="w-4 h-4 text-accent flex-shrink-0" />
                                <a
                                    href="mailto:info@nawastech.com"
                                    className="hover:text-accent transition-colors text-sm"
                                    aria-label={`${t("footer.email")}: info@nawastech.com`}>
                                    info@nawastech.com
                                </a>
                            </div>
                            <div className="flex items-center gap-3">
                                <Phone className="w-4 h-4 text-accent flex-shrink-0" />
                                <a 
                                    href="tel:+966537400097" 
                                    className="hover:text-accent transition-colors text-sm"
                                    aria-label={`${t("footer.phone")}: +966 53 740 0097`}>
                                    +966 53 740 0097
                                </a>
                            </div>
                            <div className="flex items-center gap-3">
                                <MessageCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                                <a 
                                    href="https://wa.me/966537400097"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-green-400 transition-colors text-sm"
                                    aria-label={t("footer.whatsapp")}>
                                    WhatsApp
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Payment Methods and Legal */}
                    <div className="flex flex-col items-center md:items-start space-y-4">
                        <h3 className="text-lg font-semibold">{t("footer.paymentMethods")}</h3>
                        <div className="flex justify-center md:justify-start items-center gap-3 mb-4">
                            <img
                                src="/assets/visa.png"
                                alt={t("footer.visaPayment")}
                                className="w-10 h-6 object-contain transition-transform hover:scale-105"
                                width="40"
                                height="24"
                                loading="lazy"
                                decoding="async"
                            />
                            <img
                                src="/assets/card.png"
                                alt={t("footer.mastercardPayment")}
                                className="w-12 h-12 object-contain transition-transform hover:scale-105"
                                width="48"
                                height="48"
                                loading="lazy"
                                decoding="async"
                            />
                            <img
                                src="/assets/mada.svg"
                                alt={t("footer.madaPayment")}
                                className="w-12 h-12 object-contain transition-transform hover:scale-105"
                                width="48"
                                height="48"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                        
                        {/* Legal Links */}
                        <div className="flex flex-col space-y-2">
                            <Link 
                                to="/privacy-policy" 
                                className="text-sm hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-primary rounded-sm px-1 py-1 text-center md:text-left">
                                {i18n.language === "ar" ? "سياسة الخصوصية" : "Privacy Policy"}
                            </Link>
                            <Link 
                                to="/user-agreement" 
                                className="text-sm hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-primary rounded-sm px-1 py-1 text-center md:text-left">
                                {i18n.language === "ar" ? "اتفاقية المستخدم" : "User Agreement"}
                            </Link>
                        </div>
                    </div>
                </div>
                                    
                {/* Footer Bottom */}
                <div className="mt-12 pt-8 border-t border-white/20">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
                        {/* VAT Number */}
                        <p className="text-xs text-white/60">
                            {t("footer.vatNumber", { vat: "310286964100003" })}
                        </p>
                        
                        {/* Copyright */}
                        <p className="text-sm text-white/70">
                            © {new Date().getFullYear()} NawasTech. {i18n.language === "ar" ? "جميع الحقوق محفوظة" : "All rights reserved"}
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
